/**
 * CardFase - Card individual de uma fase de produção
 */
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Clock, Play, CheckCircle2, AlertTriangle, 
  ChevronDown, ChevronRight, Eye, Camera 
} from 'lucide-react';
import { STATUS_CONFIG, calculateStatus, formatDate } from './constantes';

const CardFase = ({
  fase,
  fabrica,
  isExpanded,
  onToggleExpand,
  onIniciar,
  onConcluir,
  onVerDetalhes,
  onToggleEtapa
}) => {
  const statusCalculado = calculateStatus(fase);
  const StatusIcon = STATUS_CONFIG[statusCalculado]?.icon || Clock;
  const progressoEtapas = fase.etapas?.length > 0 
    ? Math.round((fase.etapas.filter(e => e.concluida).length / fase.etapas.length) * 100)
    : fase.progresso || 0;

  return (
    <div className="p-4" data-testid={`fase-${fase.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Nome e Status */}
          <div className="flex items-center gap-2 mb-2">
            {fase.etapas?.length > 0 && (
              <button 
                onClick={() => onToggleExpand(fase.id)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </button>
            )}
            {fase.ordem !== undefined && fase.ordem !== null && (
              <span className="text-xs font-semibold text-gray-400" data-testid={`fase-ordem-${fase.id}`}>
                #{fase.ordem}
              </span>
            )}
            <span className="font-medium">{fase.fase_nome}</span>
            <Badge className={`text-xs ${STATUS_CONFIG[statusCalculado]?.color}`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {STATUS_CONFIG[statusCalculado]?.label}
            </Badge>
          </div>
          
          {/* Informações */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div>
              <span className="text-gray-500 text-xs block">Fábrica</span>
              <span className="font-medium">{fabrica?.name || 'Não definida'}</span>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Prev. Início</span>
              <span>{formatDate(fase.data_previsao_inicio || fase.data_inicio)}</span>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Prev. Fim</span>
              <span>{formatDate(fase.data_previsao_fim || fase.data_fim)}</span>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Início Real</span>
              <span className={fase.data_inicio_real ? 'text-green-600 font-medium' : ''}>
                {fase.data_inicio_real ? formatDate(fase.data_inicio_real) : '-'}
              </span>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Progresso</span>
              <div className="flex items-center gap-2">
                <Progress value={progressoEtapas} className="h-2 flex-1" />
                <span className="text-xs font-medium">{progressoEtapas}%</span>
              </div>
            </div>
          </div>
          
          {/* Foto de conclusão */}
          {fase.foto_conclusao && (
            <div className="mt-2">
              <Badge variant="outline" className="text-green-600 bg-green-50 text-xs">
                <Camera className="h-3 w-3 mr-1" />
                Foto de conclusão anexada
              </Badge>
            </div>
          )}
        </div>
        
        {/* Ações */}
        <div className="flex flex-col gap-1">
          {statusCalculado === 'planejado' && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onIniciar(fase)}
              className="text-blue-600"
            >
              <Play className="h-3 w-3 mr-1" />
              Iniciar
            </Button>
          )}
          {(statusCalculado === 'em_andamento' || statusCalculado === 'atrasado') && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onConcluir(fase)}
              className="text-green-600"
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Concluir
            </Button>
          )}
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => onVerDetalhes(fase)}
          >
            <Eye className="h-3 w-3 mr-1" />
            Detalhes
          </Button>
        </div>
      </div>
      
      {/* Etapas expandidas */}
      {isExpanded && fase.etapas?.length > 0 && (
        <div className="mt-3 ml-8 space-y-1">
          {fase.etapas.map((etapa, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-2 p-2 rounded text-sm ${
                etapa.concluida ? 'bg-green-50' : 'bg-gray-50'
              }`}
            >
              <button 
                onClick={() => onToggleEtapa(fase, idx)}
                className="hover:scale-110 transition-transform"
              >
                {etapa.concluida ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <Clock className="h-4 w-4 text-gray-400" />
                )}
              </button>
              <span className={etapa.concluida ? 'line-through text-gray-500' : ''}>
                {etapa.nome}
              </span>
              {etapa.duracao_dias && (
                <span className="text-xs text-gray-400 ml-auto">
                  {etapa.duracao_dias}d
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardFase;
